import { AxiosResponse } from "axios";
import { AuthEnums } from "models/enums";
import { HoldingRegisterFormTypes } from "models/formTypes";
import useAPIClientHelper from "./useAPIClientHelper";
import { TranslationKeys } from "translations";
import { LocalizationHelperInstance } from "utils/helpers";
import {
  WintSuperkollAPIModelHoldingRegisters as HoldingRegistersApiModel,
  WintUtilsWebApi,
} from "wint-api-client/dist/types";

function useHoldingRegisterService() {
  const { apiClientInstance } = useAPIClientHelper();

  const filterHoldingRegisters = async (
    baseFilter: WintUtilsWebApi.Filter,
    formFilter?: HoldingRegisterFormTypes.HoldingRegisterFilter
  ): Promise<AxiosResponse<WintUtilsWebApi.ListResponse<HoldingRegistersApiModel.HoldingRegister>>> => {
    return apiClientInstance(AuthEnums.AuthType.Company).HoldingRegisterClient.Get({
      ...baseFilter,
      ...reportToAPIFilter(formFilter),
    });
  };

  const reportToAPIFilter = (
    formFilter?: HoldingRegisterFormTypes.HoldingRegisterFilter
  ): Omit<HoldingRegistersApiModel.HoldingRegisterFilter, keyof WintUtilsWebApi.Filter> => {
    return {
      assetAccountNumber: formFilter?.assetAccountNumber,
      depreciatingAccountNumber: formFilter?.depreciationAccountNumber,
      snapshot: formFilter?.snapshot ?? new Date(),
      description: formFilter?.description,
      isDeleted: formFilter?.isDeleted,
      depreciates: formFilter?.depreciates,
      isActive: formFilter?.isActive,
      startDateMin: formFilter?.startMonth?.start,
      startDateMax: formFilter?.startMonth?.end,
      terminationDateMin: formFilter?.terminationMonth?.start,
      terminationDateMax: formFilter?.terminationMonth?.end,
      hasVouchers: formFilter?.hasVouchers,
      minSerialNumber: formFilter?.serialNumber?.lowest,
      maxSerialNumber: formFilter?.serialNumber?.highest,
    };
  };

  const getBaseFilterFromHoldingRegisterFilter = (
    filter: HoldingRegisterFormTypes.HoldingRegisterFilter
  ): HoldingRegisterFormTypes.HoldingRegisterAssetsBaseFilter => {
    const assetsFilter: HoldingRegisterFormTypes.HoldingRegisterAssetsBaseFilter = {
      description: filter.description,
      isDeleted: filter.isDeleted,
      depreciates: filter.depreciates,
      isActive: filter.isActive,
      snapshot: filter.snapshot,
      startMonth: filter.startMonth,
      terminationMonth: filter.terminationMonth,
      hasVouchers: filter.hasVouchers,
      serialNumber: filter.serialNumber,
    };
    return assetsFilter;
  };

  const filterAssets = async (
    holdingRegisterId: string,
    baseFilter: WintUtilsWebApi.Filter,
    formFilter?: HoldingRegisterFormTypes.HoldingRegisterAssetsFilter
  ): Promise<AxiosResponse<WintUtilsWebApi.ListResponse<HoldingRegistersApiModel.Asset>>> => {
    return apiClientInstance(AuthEnums.AuthType.Company).HoldingRegisterClient.GetAssets({
      ...baseFilter,
      ...assetsReportToAPIFilter(holdingRegisterId, formFilter),
    });
  };

  const assetsReportToAPIFilter = (
    holdingRegisterId: string,
    formFilter?: HoldingRegisterFormTypes.HoldingRegisterAssetsFilter
  ): Omit<HoldingRegistersApiModel.AssetFilter, keyof WintUtilsWebApi.Filter> => {
    return {
      holdingRegisterId: holdingRegisterId,
      description: formFilter?.description,
      isDeleted: formFilter?.isDeleted,
      depreciates: formFilter?.depreciates,
      isActive: formFilter?.isActive,
      snapshot: formFilter?.snapshot ?? new Date(),
      startDateMin: formFilter?.startMonth?.start,
      startDateMax: formFilter?.startMonth?.end,
      terminationDateMin: formFilter?.terminationMonth?.start,
      terminationDateMax: formFilter?.terminationMonth?.end,
      hasVouchers: formFilter?.hasVouchers,
      minSerialNumber: formFilter?.serialNumber?.lowest,
      maxSerialNumber: formFilter?.serialNumber?.highest,
    };
  };

  const getValueToPrintForAssetTag = (value: boolean): string => {
    if (value) return LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Yes);
    else return LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.No);
  };

  return {
    filterHoldingRegisters,
    reportToAPIFilter,
    getBaseFilterFromHoldingRegisterFilter,
    filterAssets,
    assetsReportToAPIFilter,
    getValueToPrintForAssetTag,
  };
}

export default useHoldingRegisterService;
